
import { Record } from "../fable_modules/fable-library-js.5.3.0/Types.js";
import { list_type, class_type, record_type, string_type } from "../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { FeeAmountCentsModule_value, FeeAmountCentsModule_create, FeeAmountCents_$reflection } from "./BaseTypes.js";
import { day, month, addSeconds, create } from "../fable_modules/fable-library-js.5.3.0/DateOffset.js";
import { fromHours } from "../fable_modules/fable-library-js.5.3.0/TimeSpan.js";
import { Result_DefaultWith } from "../fable_modules/fable-library-js.5.3.0/Result.js";
import { Exception } from "../fable_modules/fable-library-js.5.3.0/Util.js";
import { tryPick, tryFind, singleton, ofArray } from "../fable_modules/fable-library-js.5.3.0/List.js";
import { filter } from "../fable_modules/fable-library-js.5.3.0/String.js";
import { isDigit } from "../fable_modules/fable-library-js.5.3.0/Char.js";
import { map, ofNullable, defaultArg } from "../fable_modules/fable-library-js.5.3.0/Option.js";
import { compare } from "../fable_modules/fable-library-js.5.3.0/Date.js";
import { item } from "../fable_modules/fable-library-js.5.3.0/Array.js";

/**
 * A charity covered by a promotion. Ein holds digits only.
 */
export class PromotedCharity extends Record {
    constructor(Ein, Slug, FeatureLabel, Region, CharityName) {
        super();
        this.Ein = Ein;
        this.Slug = Slug;
        this.FeatureLabel = FeatureLabel;
        this.Region = Region;
        this.CharityName = CharityName;
    }
}

export function PromotedCharity_$reflection() {
    return record_type("Shared.PromotedCharity", [], PromotedCharity, () => [["Ein", string_type], ["Slug", string_type], ["FeatureLabel", string_type], ["Region", string_type], ["CharityName", string_type]]);
}

/**
 * A time-boxed promotion. The window is half-open: StartsAt is inclusive, EndsAt is exclusive.
 */
export class Promotion extends Record {
    constructor(Id, StartsAt, EndsAt, FeeCents, Charities) {
        super();
        this.Id = Id;
        this.StartsAt = StartsAt;
        this.EndsAt = EndsAt;
        this.FeeCents = FeeCents;
        this.Charities = Charities;
    }
}

export function Promotion_$reflection() {
    return record_type("Shared.Promotion", [], Promotion, () => [["Id", string_type], ["StartsAt", class_type("System.DateTimeOffset")], ["EndsAt", class_type("System.DateTimeOffset")], ["FeeCents", FeeAmountCents_$reflection()], ["Charities", list_type(PromotedCharity_$reflection())]]);
}

export const PromotionModule_floodRelief2026 = new Promotion("flood-relief-2026-10", create(2026, 9, 29, 0, 0, 0, fromHours(-5)), create(2026, 11, 2, 0, 0, 0, fromHours(-6)), Result_DefaultWith((message) => {
    throw new Exception(message);
}, FeeAmountCentsModule_create(500)), ofArray([new PromotedCharity("742225369", "the-community-foundation-of-the-texas-hill-country-inc", "Texas Hill Country Flood Relief", "Texas Hill Country", "Community Foundation of the Texas Hill Country"), new PromotedCharity("951831116", "direct-relief", "Nepal Flood Relief", "Nepal", "Direct Relief")]));

export const PromotionModule_all = singleton(PromotionModule_floodRelief2026);

export function PromotionModule_normalizeEin(ein) {
    return filter(isDigit, ein);
}

function PromotionModule_promotedCharityFor(charity, p) {
    const ein = PromotionModule_normalizeEin(defaultArg(ofNullable(charity.Ein), ""));
    if (ein !== "") {
        return tryFind((c) => (c.Ein === ein), p.Charities);
    }
    else {
        const slug = defaultArg(ofNullable(charity.Slug), "").toLowerCase();
        return tryFind((c_1) => {
            if (c_1.Slug.toLowerCase() === slug) {
                return slug !== "";
            }
            else {
                return false;
            }
        }, p.Charities);
    }
}

/**
 * Whether the charity is covered by the promotion: by EIN when present, else by slug (case-insensitive).
 */
export function PromotionModule_includesCharity(charity, p) {
    return PromotionModule_promotedCharityFor(charity, p) != null;
}

export function PromotionModule_isRunning(at, p) {
    if (compare(p.StartsAt, at) <= 0) {
        return compare(at, p.EndsAt) < 0;
    }
    else {
        return false;
    }
}

/**
 * The promotion that applies to a charity at the given instant, if any.
 */
export function PromotionModule_activeFor(at, charity) {
    return tryFind((p) => {
        if (PromotionModule_isRunning(at, p)) {
            return PromotionModule_includesCharity(charity, p);
        }
        else {
            return false;
        }
    }, PromotionModule_all);
}

/**
 * Finds a promoted charity by its Every.org slug, whether or not its promotion is running.
 */
export function PromotionModule_tryFindCharityBySlug(slug) {
    const wanted = slug.toLowerCase();
    return tryPick((p) => map((c_1) => [p, c_1], tryFind((c) => (c.Slug.toLowerCase() === wanted), p.Charities)), PromotionModule_all);
}

/**
 * The fee reason recorded on ChallengeFeeSet for a fee set by this promotion.
 */
export function PromotionModule_reason(p) {
    return "promotion:" + p.Id;
}

/**
 * The promotion's discount from a standard fee as a whole percentage, rounded half up:
 * 73 for a $5.00 promotion against $18.50. Zero when there is no discount to state.
 */
export function PromotionModule_percentOff(standardCents, p) {
    const promoCents = FeeAmountCentsModule_value(p.FeeCents) | 0;
    if ((standardCents <= 0) ? true : (promoCents >= standardCents)) {
        return 0;
    }
    else {
        return ~~((((standardCents - promoCents) * 200) + standardCents) / (2 * standardCents)) | 0;
    }
}

const PromotionModule_monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * The last day of the promotion in its own offset, e.g. "November 1".
 * Built from an explicit month-name list so .NET and Fable render it identically.
 */
export function PromotionModule_endDateLabel(p) {
    const lastInstant = addSeconds(p.EndsAt, -1);
    return `${item(month(lastInstant) - 1, PromotionModule_monthNames)} ${day(lastInstant)}`;
}

//# sourceMappingURL=Promotion.js.map
